const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/components/StatusTag/StatusTag.tsx';
import { Chip } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { css } from '@emotion/css';
import { colors } from '../../defs/colors';
import Icon from '@mui/material/Icon';
import { ToolTip } from '../ToolTip/ToolTip';
import { ReactNode, useMemo } from 'react';

export type StatusTagSize = 'small' | 'medium' | undefined;

/**
 * React properties
 */
interface Props {
    icon?: ReactNode;
    caption?: string | ReactNode;
    value?: string;
    toolTip?: string | ReactNode;
    toolTipBgColor?: string;
    toolTipMinWidth?: number;
    toolTipElevation?: number;
    importance: 'normal' | 'important' | 'warn';
    bgColor?: string;
    tagClicked?: () => void;
    disabled?: boolean;
    size?: StatusTagSize;
}

// If you change this function, remember to update useMemo's dependencies in useCSSClasses as well!
const getBackgroundColor = (props: Props, theme: Theme) => {
    if (props.value === '?' && !props.bgColor) {
        return theme.palette.common.white;
    } else if (!!props.bgColor) {
        return props.bgColor;
    } else if (props.importance === 'important') {
        return theme.palette.primary.main;
    } else if (props.importance === 'warn') {
        return colors.orange['400'];
    }
    return undefined;
};

function useCSSClasses(props: Props) {
    const theme = useTheme();
    return useMemo(() => {
        const bgColor = getBackgroundColor(props, theme);
        const contrastColor = !bgColor ? undefined : theme.palette.getContrastText(bgColor);
        const isDisabled = typeof props.disabled === 'boolean' ? props.disabled : !props.tagClicked;

        return {
            root: isDisabled
                ? css`
                      background-color: ${bgColor};
                      color: ${contrastColor};
                      cursor: default;
                  `
                : css`
                      background-color: ${bgColor};
                      color: ${contrastColor};
                      cursor: default;
                      opacity: 1;

                      &:hover,
                      &:focus {
                          background-color: ${bgColor};
                          color: ${contrastColor};
                          cursor: default;
                          opacity: 0.7;
                      }
                  `,
            info: props.toolTip
                ? css`
                      color: ${!bgColor ? theme.palette.text.primary : contrastColor};
                      cursor: default;
                      font-size: ${theme.typography.subtitle1.fontSize};

                      &:hover,
                      &:focus {
                          cursor: default;
                          color: inherit;
                          font-size: ${theme.typography.subtitle1.fontSize};
                      }
                  `
                : css``,
            deleteIcon: css`
                color: ${!bgColor ? theme.palette.text.primary : contrastColor} !important;
                font-size: ${theme.typography.subtitle1.fontSize} !important;
            `,
        };
    }, [theme, props.value, props.bgColor, props.importance, props.disabled, props.tagClicked, props.toolTip]);
}

/**
 * Functional React component
 */
export const StatusTag = (props: Props) => {
    const classes = useCSSClasses(props);

    const disabled = typeof props.disabled === 'boolean' ? props.disabled : !props.tagClicked;
    let label: string | ReactNode;
    let icon: any = undefined;

    if (!!props.icon) {
        if (!props.caption) {
            label = props.icon;
        } else {
            icon = props.icon;
            label = !props.value ? `${props.caption}` : `${props.caption}: ${props.value}`;
        }
    } else {
        label = !props.value ? `${props.caption}` : `${props.caption}: ${props.value}`;
    }

    return (
        <ToolTip
            title={props.toolTip}
            backgroundColor={props.toolTipBgColor}
            contentMinWidth={props.toolTipMinWidth}
            elevation={props.toolTipElevation}
            wrapNewlines={true}
        >
            <Chip
                icon={icon}
                classes={{
                    root: classes.root,
                    deleteIcon: classes.deleteIcon,
                }}
                size={props.size === 'small' ? 'small' : 'medium'}
                label={label}
                variant={props.value === '?' && !props.bgColor ? 'outlined' : 'filled'}
                onClick={disabled ? undefined : props.tagClicked}
                onDelete={props.toolTip ? () => {} : undefined}
                deleteIcon={<Icon className={'icons8-info'} classes={{ root: classes.info }} />}
            />
        </ToolTip>
    );
};
